/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Icon,
  SearchInput,
  Table,
  Typography,
  useForm,
  useTheme,
  UtilityButton,
  Pagination,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import {
  searchWallets,
  type SearchWalletsFilters,
  type SearchWalletsResponse,
} from 'components/Wallets/api';

const PAGE_SIZE = 25;

export const WalletList = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const headers = [
    { label: 'Address', cellKey: 'address', sort: false },
    { label: 'Currency', cellKey: 'currencyCode', sort: false },
    { label: 'Type', cellKey: 'walletType', sort: false },
    { label: 'User ID', cellKey: 'userId', sort: false },
    { label: 'Disabled', cellKey: 'disabled', sort: false },
  ];

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: SearchWalletsFilters;
    pagination: { page: number; size: number };
  } | null>('qbWalletsFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<SearchWalletsFilters>(
    {
      address: filterStore?.filters.address ?? undefined,
      currency: filterStore?.filters.currency ?? undefined,
    },
    {}
  );

  const [searchResult, setSearchStore] =
    useSessionStorage<SearchWalletsResponse>('walletsSearchResult', {
      wallets: [],
      aggregatedData: { count: 0 },
    });

  const normalizedFilters: SearchWalletsFilters = useMemo(
    () => ({
      address: fields.address?.trim() || undefined,
      currency: fields.currency ? fields.currency.toUpperCase() : undefined,
    }),
    [fields]
  );

  const [{ loading }, search] = useAsyncFn(async () => {
    const appliedFilters = {
      filters: normalizedFilters,
      pagination: { page, size: PAGE_SIZE },
    };
    const data = await searchWallets(appliedFilters);
    setFilterStore(appliedFilters);
    setSearchStore(data);
    return data;
  }, [page, normalizedFilters]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );

  const rows = useMemo(() => {
    return searchResult.wallets.map(
      ({ uuid, address, currencyCode, walletType, userId, disabled }) => ({
        value: {
          address: address ? <TruncatedString text={address} /> : '—',
          currencyCode,
          walletType,
          userId: <TruncatedString text={userId} />,
          disabled: disabled ? 'Yes' : 'No',
          uuid,
        },
      })
    );
  }, [searchResult]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set({ address: undefined, currency: undefined });
  };

  return (
    <>
      <Header>
        <Typography variant="subtitle">Wallets</Typography>
      </Header>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto 1fr',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="Address"
          value={fields.address ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.address?.set(e.target.value);
          }}
        />
        <SearchInput
          placeholder="Currency"
          value={fields.currency ?? ''}
          onChange={(e) => {
            setPage(1);
            setters.currency?.set(e.target.value);
          }}
        />
      </div>

      <div
        css={{
          marginTop: spacing.xxs,
          marginBottom: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'auto auto 1fr',
          alignItems: 'center',
          gap: spacing.xxxs,
        }}
      >
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={search}
          disabled={loading}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>

      <Table
        headers={headers}
        rows={rows}
        // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
        onRowClick={(row) => navigate(`${row.uuid}`)}
      />

      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={searchResult.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
